/**
 * What a session's prints left behind, from the tape alone.
 *
 * Open interest cannot answer this for the session currently trading — it is published once,
 * before the open, so today's figure arrives tomorrow. But the tape can answer a large part
 * of it immediately: 5,000 contracts bought and 5,000 sold later the same day is a position
 * opened and closed inside the session, and nothing was left at the end of it. That is
 * visible in the prints as they happen.
 *
 * The running net is the sum of aggressor-side volume: buys add, sells subtract. Its peak is
 * how large the exposure got; the value at the end is what survived the session. The gap
 * between them is volume that round-tripped — real trading that left no position.
 *
 * **What this is not.** The aggressor side says which way each print leaned, not who was on
 * the other end. A 5,000 buy and a later 5,000 sell may be one trader closing out, or two
 * unrelated traders. Net tape direction is the honest name for it, and it is reported as
 * that rather than as a proven round trip. It is still the strongest thing available before
 * the next morning's open-interest print, and when the net returns to zero after a large
 * peak the ordinary reading is that the position was closed.
 */

export interface SessionPrint {
  size?: number | null;
  execution_side?: string | null;
  /** Sort key; prints are ordered before the running net is computed. */
  datetime?: string | null;
  time?: string | null;
}

export interface SessionNetPosition {
  buyVolume: number;
  sellVolume: number;
  /** Volume the classifier could not put on a side; excluded from the net, not counted as neutral. */
  undirectedVolume: number;
  /** Buys minus sells at the end of the session — what the tape left behind. */
  netAtClose: number;
  /** Largest absolute net reached during the session. */
  peakNet: number;
  /** Volume that was taken on and given back inside the session. */
  roundTripped: number;
  /** roundTripped as a share of peak exposure, 0–1. 1 means everything was given back. */
  roundTripShare: number | null;
}

export const EMPTY_SESSION_NET: SessionNetPosition = {
  buyVolume: 0,
  sellVolume: 0,
  undirectedVolume: 0,
  netAtClose: 0,
  peakNet: 0,
  roundTripped: 0,
  roundTripShare: null,
};

function sortKey(p: SessionPrint): string {
  return String(p.datetime || p.time || '');
}

/**
 * Roll the session's prints into a net position.
 *
 * Prints are sorted first: the peak is path-dependent, so an out-of-order tape would report
 * a peak that never happened.
 */
export function sessionNetPosition(prints: SessionPrint[]): SessionNetPosition {
  if (!Array.isArray(prints) || prints.length === 0) return EMPTY_SESSION_NET;

  const ordered = prints.slice().sort((a, b) => sortKey(a).localeCompare(sortKey(b)));

  let buyVolume = 0;
  let sellVolume = 0;
  let undirectedVolume = 0;
  let running = 0;
  let peakNet = 0;

  for (const p of ordered) {
    const size = Number(p.size);
    if (!Number.isFinite(size) || size <= 0) continue;
    const side = String(p.execution_side ?? '').trim().toLowerCase();
    if (side === 'buy') {
      buyVolume += size;
      running += size;
    } else if (side === 'sell') {
      sellVolume += size;
      running -= size;
    } else {
      undirectedVolume += size;
      continue;
    }
    peakNet = Math.max(peakNet, Math.abs(running));
  }

  const netAtClose = running;
  const roundTripped = Math.max(0, peakNet - Math.abs(netAtClose));

  return {
    buyVolume,
    sellVolume,
    undirectedVolume,
    netAtClose,
    peakNet,
    roundTripped,
    roundTripShare: peakNet > 0 ? roundTripped / peakNet : null,
  };
}

export type SessionOutcome =
  | 'Round-tripped'
  | 'Mostly round-tripped'
  | 'Net long at close'
  | 'Net short at close'
  | null;

/**
 * How the session ended, in one phrase.
 *
 * `Round-tripped` is reserved for a net that came back to within a tenth of its peak — the
 * 5,000-bought-then-5,000-sold case, where the tape took on a position and gave essentially
 * all of it back. Anything still holding more than half its peak is reported by direction
 * instead, because that is a position left open, not a round trip.
 */
export function sessionOutcome(net: SessionNetPosition): SessionOutcome {
  if (net.peakNet <= 0) return null;
  const share = net.roundTripShare ?? 0;
  if (share >= 0.9) return 'Round-tripped';
  if (share >= 0.5) return 'Mostly round-tripped';
  return net.netAtClose >= 0 ? 'Net long at close' : 'Net short at close';
}
